import { Array as Arr, Data, Effect, Equal, Match, Option, Predicate, Stream } from 'effect';
import type { Duration } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';

import { facetKeyEquivalence } from '../../capability/productListing/facetKey';
import type { FacetKey } from '../../capability/productListing/facetKey';
import type { ListingScopeKey } from '../../capability/productListing/listingScope';
import type { ListingChange, ListingState } from '../../capability/productListing/listingSelection';

/** The two ends of one range facet, in listing units. `none` is an open end. */
/** @stability unstable */
export type RangeBounds = {
  readonly min: Option.Option<number>;
  readonly max: Option.Option<number>;
};

/**
 * What a range control shows, and on whose authority.
 *
 * `Pending` holds bounds the user is still moving. `from` records the committed
 * bounds the movement started from, so a committed selection that differs from
 * it is known to come from elsewhere — Back, Forward, a cleared filter — and
 * wins over the draft.
 */
type RangeDraft = Data.TaggedEnum<{
  Idle: object;
  Pending: { readonly bounds: RangeBounds; readonly from: RangeBounds };
}>;

const RangeDraft = Data.taggedEnum<RangeDraft>();

type RangeScope = { readonly scopeKey: ListingScopeKey; readonly key: FacetKey };

const unbounded: RangeBounds = { min: Option.none(), max: Option.none() };

const sameBounds = (self: RangeBounds, that: RangeBounds): boolean =>
  Equal.equals(self.min, that.min) && Equal.equals(self.max, that.max);

const committedBounds = (state: ListingState, key: FacetKey): RangeBounds =>
  Option.match(
    Arr.findFirst(state.selection.ranges, (range) => facetKeyEquivalence(range.key, key)),
    {
      onNone: () => unbounded,
      onSome: ({ min, max }): RangeBounds => ({
        min: Option.fromNullishOr(min),
        max: Option.fromNullishOr(max),
      }),
    }
  );

/** A draft is current while the committed bounds are still the ones it started from. */
const currentDraft = (draft: RangeDraft, committed: RangeBounds): Option.Option<RangeBounds> =>
  Match.value(draft).pipe(
    Match.tag('Idle', () => Option.none()),
    Match.tag('Pending', ({ bounds, from }) =>
      sameBounds(from, committed) ? Option.some(bounds) : Option.none()
    ),
    Match.exhaustive
  );

const toChange = (key: FacetKey, bounds: RangeBounds): ListingChange =>
  Option.isNone(bounds.min) && Option.isNone(bounds.max)
    ? { _tag: 'ClearKey', key }
    : {
        _tag: 'SetRange',
        key,
        min: Option.getOrNull(bounds.min),
        max: Option.getOrNull(bounds.max),
      };

/**
 * Debounces range controls into listing changes.
 *
 * Each `(scopeKey, key)` pair gets one writable atom. A write shows at once as
 * the atom's value. One `SetRange` (or `ClearKey`, for two open ends) follows
 * after `delay` without a further write, so a dragged slider moves browser
 * history once per pause instead of once per pointer event.
 *
 * A pending draft is dropped when the committed bounds change underneath it,
 * and when the last reader unmounts. The commit stream runs on `runtime`, so
 * the delay follows that runtime's `Clock`.
 */
/** @stability unstable */
export const makeListingRangeDraft = <R, ER>(deps: {
  readonly runtime: Atom.AtomRuntime<R, ER>;
  readonly state: (scopeKey: ListingScopeKey) => Atom.Atom<ListingState>;
  readonly change: (scopeKey: ListingScopeKey) => Atom.Writable<ListingState, ListingChange>;
  readonly delay: Duration.Duration;
}): ((scopeKey: ListingScopeKey, key: FacetKey) => Atom.Writable<RangeBounds, RangeBounds>) => {
  const draft = Atom.family((_scope: RangeScope) => Atom.make<RangeDraft>(RangeDraft.Idle()));

  const commit = Atom.family((scope: RangeScope) =>
    deps.runtime.atom((get) =>
      // The stream starts from the current draft, so a write that lands before
      // the stream subscribes still commits.
      get.stream(draft(scope)).pipe(
        Stream.filter(Predicate.isTagged('Pending')),
        Stream.debounce(deps.delay),
        Stream.runForEach((pending) =>
          Effect.sync(() => {
            // A newer write is still inside its own debounce window; it commits later.
            if (get.once(draft(scope)) !== pending) return;
            const committed = committedBounds(get.once(deps.state(scope.scopeKey)), scope.key);
            get.set(draft(scope), RangeDraft.Idle());
            const fresh = Option.filter(
              currentDraft(pending, committed),
              (bounds) => !sameBounds(bounds, committed)
            );
            if (Option.isSome(fresh)) {
              get.set(deps.change(scope.scopeKey), toChange(scope.key, fresh.value));
            }
          })
        )
      )
    )
  );

  const range = Atom.family((scope: RangeScope) =>
    Atom.writable(
      (get): RangeBounds => {
        get.mount(commit(scope));
        const committed = committedBounds(get(deps.state(scope.scopeKey)), scope.key);
        return Option.getOrElse(currentDraft(get(draft(scope)), committed), () => committed);
      },
      (context, bounds: RangeBounds) =>
        context.set(
          draft(scope),
          RangeDraft.Pending({
            bounds,
            from: committedBounds(context.get(deps.state(scope.scopeKey)), scope.key),
          })
        )
    )
  );

  return (scopeKey, key) => range({ scopeKey, key });
};
